import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { __awaiter } from 'tslib';

const TOP = 'top';
const BOTTOM = 'bottom';
const LEFT = 'left';
const RIGHT = 'right';
const CENTER = 'center';
const HIDDEN = 'hidden';
const ARROW_SPACING = 6;
const CLASS_PREFIX = 'se-fe-position';
const POSITION_CLASSES = 'left right center top bottom undefined'.split(' ').map(dir => `${CLASS_PREFIX}--${dir}`);
// TODO: make these events mobile friendly
const HOVER_EVENTS = ['hover'];
const CLICK_EVENTS = ['click'];
class SeFePositionService {
    constructor() {
        this.toggling = false;
        this.triggers = [];
        this.contents = [];
        this.enableTopLevelScrollParent = false;
        this.getVisibleSize = (containerStart, containerEnd, triggerStart, contentOffset, contentSize) => {
            const start = Math.max(containerStart, triggerStart + contentOffset);
            const end = Math.min(containerEnd, triggerStart + contentOffset + contentSize);
            return end - start;
        };
        this.pull = (arr, values) => {
            return arr.filter((item) => {
                return values.indexOf(item) < 0;
            });
        };
        this.documentElement.addEventListener('click', e => this.hideActiveContent(e));
        this.window().addEventListener('resize', () => this.hideActiveContent());
    }
    addTrigger(instance) {
        const events = this.triggerEvents(instance === null || instance === void 0 ? void 0 : instance.event);
        const toggle = () => this.toggleContent(instance);
        const open = () => this.openContent(instance);
        const close = () => this.closeContent(instance);
        const trigger = { instance, toggle, open, close };
        if (events)
            events.forEach(e => instance.element.addEventListener(e, toggle));
        this.triggers.push(trigger);
    }
    removeTrigger(instance) {
        const trigger = this.triggers.find(t => t.instance === instance);
        if (!trigger)
            return;
        const events = this.triggerEvents(instance === null || instance === void 0 ? void 0 : instance.event);
        if (events)
            events.forEach(e => instance.element.removeEventListener(e, trigger.toggle));
        this.triggers = this.pull(this.triggers, [trigger]);
    }
    addContent(content) {
        this.setInitalPlacement(content.element); //prevents content from adding white space at the bottom
        this.enableTopLevelScrollParent = content.enableTopLevelScrollParent;
        this.contents.push(content);
    }
    removeContent(content) {
        this.contents = this.pull(this.contents, [content]);
    }
    getScrollParent(el) {
        const scrollParentArr = [];
        const win = this.window();
        let parent = this.parentElement(el);
        while (parent) {
            const style = win.getComputedStyle(parent);
            const overflow = `${style.overflow}${style.overflowY}`;
            if (overflow.match(/scroll|auto/))
                scrollParentArr.push(parent);
            el = parent;
            parent = this.parentElement(el);
        }
        return this.enableTopLevelScrollParent ? scrollParentArr.pop() : scrollParentArr.shift();
    }
    getScroll(el) {
        const parent = this.getScrollParent(el);
        const parentOffset = parent === el.offsetParent;
        return { top: parentOffset ? parent.scrollTop : 0, left: parentOffset ? parent.scrollLeft : 0 };
    }
    parentElement(el) {
        var _a;
        return el.parentElement || ((_a = el.parentNode) === null || _a === void 0 ? void 0 : _a.host); // parentNode.host walks through shadow roots
    }
    triggerEvents(triggerEvent) {
        if (triggerEvent === 'none')
            return; // none is for manual trigger only (keep click as default to avoid breaking change)
        return triggerEvent === 'hover' ? HOVER_EVENTS : CLICK_EVENTS;
    }
    hideActiveContent(e) {
        if (this.toggling || !this.activeName || this.clickedInside(e))
            return;
        this.contents.forEach(content => {
            content.name === this.activeName && content.hide();
            this.currentParentElement.removeEventListener('scroll', this.updatePos);
        });
        delete this.activeName;
    }
    clickedInside(e) {
        if (!e || !e.target) {
            return;
        }
        const conentElements = this.contents.map(c => c.element);
        const path = e.composedPath();
        return conentElements.some((el) => {
            return path.includes(el);
        });
    }
    closeContent(trigger) {
        if (this.activeName === trigger.name) {
            this.hideActiveContent();
        }
    }
    openContent(trigger) {
        if (this.activeName === trigger.name)
            return;
        this.hideActiveContent();
        this.activeName = trigger.name;
        this.contents.forEach(content => {
            if (content.name === trigger.name) {
                this.updatePosition(trigger, content);
                this.currentParentElement = this.getScrollParent(trigger.element) || document;
                this.updatePos = () => this.updatePosition(trigger, content);
                this.currentParentElement.addEventListener('scroll', this.updatePos);
            }
        });
        this.toggling = true;
        setTimeout(() => this.toggling = false);
    }
    toggleContent(trigger) {
        const isActiveTrigger = this.activeName === trigger.name;
        this.hideActiveContent();
        if (!isActiveTrigger) {
            this.activeName = trigger.name;
            this.contents.forEach(content => {
                if (content.name === trigger.name) {
                    this.updatePosition(trigger, content);
                    this.currentParentElement = this.getScrollParent(trigger.element) || document;
                    this.updatePos = () => this.updatePosition(trigger, content);
                    this.currentParentElement.addEventListener('scroll', this.updatePos);
                }
            });
        }
        this.toggling = true;
        setTimeout(() => this.toggling = false);
    }
    updatePosition(trigger, content) {
        return __awaiter(this, void 0, void 0, function* () {
            content.element.style.position = content.element && content.fixedPosition ? 'fixed' : 'absolute';
            yield content.show();
            // Find element positions
            const triggerPos = trigger.element.getBoundingClientRect();
            const scroll = this.getScroll(content.element);
            const scrollParentPos = this.scrollParentPosition(trigger.element);
            const { offsetParent } = content.element;
            const offsetPos = offsetParent ? offsetParent.getBoundingClientRect() : document.body.getBoundingClientRect();
            const contentPos = content.element.getBoundingClientRect(); // for width & height only
            const contentSpacing = content.spacing ? content.spacing : 0;
            const left = triggerPos.left - offsetPos.left + scroll.left; // trigger left position relative to offset parent
            const top = triggerPos.top - offsetPos.top + scroll.top; // trigger top position relative to offset parent
            // content left/top offset relative to trigger
            const contentOffsets = {
                left: 0,
                right: 0 - contentPos.width + triggerPos.width,
                center: 0 - contentPos.width / 2 + triggerPos.width / 2,
                top: 0 - contentSpacing - contentPos.height,
                bottom: triggerPos.height + contentSpacing,
            };
            // Calculate potential left/top positions (for each alignment)
            const posValues = {
                left: left,
                right: left + contentOffsets.right,
                center: left + contentOffsets.center,
                top: top + contentOffsets.top,
                bottom: top + contentOffsets.bottom,
            };
            const horiz = this.setPosition(content.element, scrollParentPos.left, scrollParentPos.right, triggerPos.left, triggerPos.right, contentOffsets, contentPos.width, this.hPositions(content), posValues, 'right', 'left');
            const vert = this.setPosition(content.element, scrollParentPos.top, scrollParentPos.bottom, triggerPos.top, triggerPos.bottom, contentOffsets, contentPos.height, this.vPositions(content), posValues, 'bottom', 'top');
            content.element.classList.remove(...POSITION_CLASSES);
            // Hide content on vert scroll when trigger is off screen
            const triggerBottom = triggerPos.bottom - scrollParentPos.top;
            const triggerTop = scrollParentPos.bottom - triggerPos.top;
            if (triggerBottom < 0 || triggerTop < 0)
                this.closeContent(trigger);
            content.element.classList.add(`${CLASS_PREFIX}--${horiz}`, `${CLASS_PREFIX}--${vert}`);
            const arrow = content.element.querySelectorAll('.se-fe-popover__arrow, .se-fe-tooltip__arrow')[0];
            // Position arrow based on content position
            if (arrow) {
                // Remove any previous styles on arrows
                arrow.removeAttribute('style');
                switch (horiz) {
                    case 'left':
                        arrow.style.left = `${triggerPos.width / 2}px`;
                        break;
                    case 'right':
                        arrow.style.right = `${triggerPos.width / 2 - ARROW_SPACING}px`;
                        break;
                    case 'center':
                        arrow.style.left = `50%`;
                        break;
                }
            }
        });
    }
    setPosition(el, containerStart, containerEnd, triggerStart, triggerEnd, contentOffsets, contentSize, positions, positionValues, positionAfter, positionBefore) {
        let visibleSize;
        let maxSize = 0;
        let position;
        let shift = 0;
        const spaceBefore = triggerStart - containerStart;
        const spaceAfter = containerEnd - triggerEnd;
        const space = containerEnd - containerStart;
        const defaultPosition = positions.includes('left') ? 0 : positionValues[positionAfter];
        // TODO: do we want to default to zero if not enough space (for hrizontal)?
        if (contentSize >= space) {
            el.style[positionBefore] = `${defaultPosition}px`;
            return positionAfter;
        }
        for (let pos of positions) {
            visibleSize = this.getVisibleSize(containerStart, containerEnd, triggerStart, contentOffsets[pos], contentSize);
            if (visibleSize >= contentSize && visibleSize > maxSize) {
                position = pos;
                maxSize = visibleSize;
                break;
            }
        }
        if (!position) {
            const horiz = positionBefore === 'left';
            const before = spaceBefore > spaceAfter;
            if (positionBefore === 'left') {
                position = before ? positionAfter : positionBefore;
            }
            else {
                position = before ? positionBefore : positionAfter;
            }
            shift = before ? spaceBefore - contentSize : contentSize - spaceAfter;
        }
        el.style[positionBefore] = `${positionValues[position] - shift}px`;
        return position;
    }
    hPositions(content) {
        if (content.position.includes(LEFT))
            return [LEFT, RIGHT, CENTER];
        if (content.position.includes(RIGHT))
            return [RIGHT, LEFT, CENTER];
        return [CENTER, LEFT, RIGHT, HIDDEN];
    }
    vPositions(content) {
        if (content.position.includes(BOTTOM))
            return [BOTTOM, TOP];
        if (content.position.includes(TOP))
            return [TOP, BOTTOM];
        return [TOP, BOTTOM, HIDDEN];
    }
    // Method accessor for easy test stubbing
    window() {
        return window;
    }
    // Method accessor for easy test stubbing
    get documentElement() {
        return document.documentElement;
    }
    scrollParentPosition(element) {
        const parent = this.getScrollParent(element);
        if (parent)
            return parent.getBoundingClientRect();
        const width = this.documentElement.clientWidth;
        const height = this.documentElement.clientHeight;
        return {
            width,
            height,
            left: 0,
            top: 0,
            right: width,
            bottom: height,
        };
    }
    setInitalPlacement(el) {
        el.setAttribute('style', 'top: 0; left: 0;');
    }
}
SeFePositionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFePositionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class SeFePositionModule {
}
SeFePositionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFePositionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, imports: [CommonModule] });
SeFePositionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, providers: [SeFePositionService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [],
                    exports: [],
                    providers: [SeFePositionService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFePositionModule, SeFePositionService };
